import Chip, { type ChipProps } from '@ixigo/iui/Chip';
import { useTimer } from '@repo/shared/hooks';
import { memo } from 'react';
import type { CabUpdateResponse, WaitingInfo } from '@/api';
import { formatDigitalTime } from './helper';

const msgStatusToChipColor: Record<WaitingInfo['status'], ChipProps['color']> =
	{
		info: 'selection',
		warning: 'warning',
		critical: 'critical',
	};

interface WaitingTimeChipProps
	extends Required<Pick<CabUpdateResponse, 'waitingInfo'>> {
	variant?: ChipProps['variant'];
}

/**
 * Memoized chip component that displays waiting time with countdown or count-up functionality
 * Only re-renders when timerValueInMs or msgStatus changes
 */
export const WaitingTimeChip = memo<WaitingTimeChipProps>(
	({ waitingInfo, variant = 'solid' }) => {
		const { timerValueInMs, status } = waitingInfo;
		const direction = status === 'critical' ? 'up' : 'down';

		// Use the simplified timer hook - no need for manual startTimeRef management
		const secondsRemaining = useTimer(
			direction === 'up'
				? {
						direction: 'up',
						elapsedTimeMs: timerValueInMs, // Baseline elapsed time
					}
				: {
						direction: 'down',
						durationMs: timerValueInMs, // Duration to count down from
					},
		);
		const time = formatDigitalTime(secondsRemaining);
		return (
			<Chip
				size="sm"
				color={msgStatusToChipColor[status]}
				variant={variant}
				className="[&>span]:tabular-nums min-w-11 justify-center"
			>
				{time}
			</Chip>
		);
	},
);
